<template>
    <Menubar :model="menuItems" class="shadow-[0_3px_3px_rgba(0,0,0,0.05)] !rounded-none">
        <template #start>
            <img src="@/assets/images/meetspain.svg" class="w-10 mr-2" />
        </template>
        <template #item="{ item, props, hasSubmenu, root }">
            <router-link v-if="item.route" v-slot="{ href, navigate }" :to="{ name: item.route, params: item.params }" custom>
                <a :href="href"
                   @click="navigate"
                   v-bind="props.action"
                   class="flex items-center"
                   :class="{
                       'bg-emphasis rounded-[4px]': item.route == $route.name
                   }"
                >
                    <span>{{ item.label }}</span>
                </a>
            </router-link>
            <template v-if="!item.route">
                <template v-if="item.params.type == 'new-contact'">
                    <a class="flex items-center" @click="showNewContactDialog = true" v-bind="props.action">
                        <span>{{ item.label }}</span>
                    </a>
                </template>
                <template v-else>
                    <a class="flex items-center" v-bind="props.action">
                        <span>{{ item.label }}</span>
                        <i v-if="hasSubmenu" :class="['pi pi-angle-down ml-auto', { 'pi-angle-down': root, 'pi-angle-right': !root }]"></i>
                    </a>
                </template>
            </template>
        </template>
        <template #end>
            <div class="flex flex-row items-center gap-4">
                <div v-if="is('manager|admin')">
                    <Button severity="secondary" size="small" label="Dokumentai" @click="toggleDocumentsMenu" />

                    <Menu ref="documentsMenu" :model="documentsMenuItems" :popup="true">
                        <template #item="{ item, props }">
                            <a :href="item.url" target="_blank" v-bind="props.action">
                                <span>{{ item.label }}</span>
                            </a>
                        </template>
                    </Menu>
                </div>
                <div class="flex items-center gap-2">
                    <img src="@/assets/images/profile.png" class="w-10 cursor-pointer" @click="toggleUserMenu" />

                    <Menu ref="userMenu" :model="userMenuItems" :popup="true">
                        <template #item="{ item, props }">
                            <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
                                <a :href="href" v-bind="props.action" @click="navigate">
                                    <span :class="item.icon" />
                                    <span class="ml-2">{{ item.label }}</span>
                                </a>
                            </router-link>
                            <a v-else :href="item.url" :target="item.target" v-bind="props.action" @click="item.action">
                                <span :class="item.icon" />
                                <span class="ml-2">{{ item.label }}</span>
                            </a>
                        </template>
                    </Menu>
                </div>
            </div>
        </template>
    </Menubar>

    <NewContact v-model:showDialog="showNewContactDialog"></NewContact>
</template>

<script setup lang="ts">
    // System imports
    import { ref, onMounted } from "vue";
    import { Auth } from '@/stores/auth';
    import { useRoute, useRouter } from 'vue-router';
    import { is } from '@/extensions/permissions.ts'
    import type { RouteRecordRaw } from 'vue-router';

    // Local imports
    import { getTenantRoutes } from '@/router/routes'
    import { apiRequest } from '@/services/axios'
    import NewContact from '@/pages/contacts/dialogs/NewContact.vue'

    // PrimeVue imports
    import Menubar from 'primevue/menubar';
    import Menu from 'primevue/menu';
    import Button from 'primevue/button';

    const routes = ref();
    let auth = Auth();
    const route = useRoute();
    const router = useRouter();
    const userMenu = ref();
    let menuItems = ref<Array<any>>([]);
    let settingsDropdownItems = ref<Array<any>>([]);
    const showNewContactDialog = ref<boolean>(false);
    const documentsMenu = ref();

    // Hooks
    onMounted(async () => {
        routes.value = await getTenantRoutes();

        updateMenuItems()

        if (is('manager|admin')) {
            loadStaticDocuments();
        }
    });

    // User menu
    const userMenuItems = ref([
        {
            label: 'Atsijungti',
            icon: 'pi pi-sign-out',
            action: async () => {
                await apiRequest(
                    'post',
                    '/auth/logout'
                );

                auth.invalidateAuth();

                router.push({ name: 'login' });
            }
        }
    ]);
    const documentsMenuItems = ref();

    // Methods
    async function loadStaticDocuments() {
        let response = await apiRequest(
            'get',
            '/documents/static-documents'
        );

        documentsMenuItems.value = response.data.data.documents;
    }

    function updateMenuItems() {
        menuItems.value = [];
        settingsDropdownItems.value = [];

        for (const [, routeItemRaw] of Object.entries(routes.value)) {
            const routeItem = routeItemRaw as RouteRecordRaw;

            if (routeItem.meta?.menu) {
                const routeRole = routeItem.meta.role;
                const hideMenuForRoles = routeItem.meta.hideMenuForRoles;

                if (typeof routeRole === 'string' && !is(routeRole)) {
                    continue;
                }

                if (typeof hideMenuForRoles === 'string' && is(hideMenuForRoles)) {
                    continue;
                }

                menuItems.value.push({
                    params: routeItem.meta.defaultType ? { type: routeItem.meta.defaultType } : {},
                    label: routeItem.meta.menuName,
                    active: route.name === routeItem.name,
                    route: routeItem.name
                });
            }
        }

        if (!is('partner_collab|partner_readonly')) {
            menuItems.value.push({
                params: {
                    type: 'new-contact'
                },
                label: 'Naujas kontaktas',
                active: false,
                route: null
            })
        }
    }

    function toggleUserMenu (event: Event) {
        userMenu.value.toggle(event);
    }

    function toggleDocumentsMenu(event: Event) {
        documentsMenu.value.toggle(event);
    }
</script>
